import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Menu, MenuItem, Checkbox, FormControlLabel } from '@mui/material'
import { capitalize, get } from 'lodash'
import { useSelector, shallowEqual } from 'react-redux'
import { toast } from 'react-toastify'
import T from '../../../../T'
import { useSendResetPasswordEmailGcpMutation } from '../../../../api/customer-portal/sendResetPasswordEmail'
import { useUnlinkUserMutation } from '../../../../api/customer-portal/unlinkUser'
import { handleError } from '../../../../utils/error'
import HHConfirmDialog from '../../../common/HHConfirmDialog'
import CustomerPortalEmailActivityDialog from './CustomerPortalEmailActivityDialog'

const CustomerPortalContactsActionMenu = ({ anchorEl, onClose, accountId, selectedContact }) => {
  const [sendResetPasswordEmailGcp, { isLoading: isSendResetPasswordEmailGcpLoading }] = useSendResetPasswordEmailGcpMutation()
  const [unlinkUser, { isLoading: isUnlinkUserLoading }] = useUnlinkUserMutation()
  const [isEmailActivityDialogOpen, setIsEmailActivityDialogOpen] = useState(false)
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false)
  const [isDisconnectUserConfirmDialogOpen, setIsDisconnectUserConfirmDialogOpen] = useState(false)
  const [isDisconnectAcknowledgeChecked, setIsDisconnectAcknowledgeChecked] = useState(false)
  const businessId = useSelector(state => state.AuthReducer.userInfo.businessInfo.businessId, shallowEqual)
  const email = get(selectedContact, 'email')

  const handleOpenEmailActivityDialog = () => {
    onClose()
    setIsEmailActivityDialogOpen(true)
  }

  const handleCloseEmailActivityDialog = () => setIsEmailActivityDialogOpen(false)

  const handleOpenResendPasswordDialog = () => {
    onClose()
    setIsResetPasswordDialogOpen(true)
  }

  const handleCloseResendPasswordDialog = () => setIsResetPasswordDialogOpen(false)

  const handleSendResetPasswordEmail = () => {
    sendResetPasswordEmailGcp({ email, businessId })
      .unwrap()
      .then(() => {
        handleCloseResendPasswordDialog()
        toast.success(T.RESET_PASSWORD_EMAIL_SENT_SUCCESSFULLY)
      })
      .catch(handleError)
  }

  const handleOpenDisconnectUserConfirmation = () => {
    onClose()
    setIsDisconnectAcknowledgeChecked(false)
    setIsDisconnectUserConfirmDialogOpen(true)
  }

  const handleCloseDisconnectUserConfirmation = () => {
    setIsDisconnectUserConfirmDialogOpen(false)
    setIsDisconnectAcknowledgeChecked(false)
  }

  const handleDisconnectUser = () =>
    unlinkUser({ accountId, payload: { emails: [email] } })
      .unwrap()
      .then(() => {
        handleCloseDisconnectUserConfirmation()
        toast.success(T.USER_DISCONNECTED_SUCCESSFULLY)
      })
      .catch(handleError)

  return (
    <>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ variant: 'outlined' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        disableScrollLock
      >
        <MenuItem onClick={handleOpenEmailActivityDialog}>{T.EMAIL_ACTIVITY}</MenuItem>
        <MenuItem onClick={handleOpenResendPasswordDialog}>{capitalize(T.RESET_PASSWORD)}</MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={handleOpenDisconnectUserConfirmation}>
          {T.DISCONNECT_USER}
        </MenuItem>
      </Menu>
      <CustomerPortalEmailActivityDialog
        isOpen={isEmailActivityDialogOpen}
        emailId={email}
        accountId={accountId}
        onClose={handleCloseEmailActivityDialog}
      />
      <HHConfirmDialog
        DialogProps={{ maxWidth: 'xs' }}
        isOpen={isResetPasswordDialogOpen}
        confirmTitle={capitalize(T.RESET_PASSWORD)}
        ConfirmButtonProps={{ loading: isSendResetPasswordEmailGcpLoading, color: 'primary' }}
        confirmDescription={T.SEND_RESET_PASSWORD_EMAIL}
        confirmButtonTitle={T.OK}
        onConfirm={handleSendResetPasswordEmail}
        onClose={handleCloseResendPasswordDialog}
      />
      <HHConfirmDialog
        DialogProps={{ maxWidth: 'sm' }}
        isOpen={isDisconnectUserConfirmDialogOpen}
        confirmTitle="⚠️ Confirm disconnect user"
        ConfirmButtonProps={{ loading: isUnlinkUserLoading, color: 'error', disabled: !isDisconnectAcknowledgeChecked }}
        confirmDescription={
          <>
            <p>By disconnecting this portal user, their email can no longer be used to create a new portal account.</p>
            <p>
              If the customer is having login issues, direct them to use “Trouble signing in” instead. Only select Disconnect if this
              customer should no longer have portal access.
            </p>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDisconnectAcknowledgeChecked}
                  onChange={e => setIsDisconnectAcknowledgeChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={T.PERMANENT_ACTION_CONFIRMATION}
            />
          </>
        }
        confirmButtonTitle="Disconnect"
        onConfirm={handleDisconnectUser}
        onClose={handleCloseDisconnectUserConfirmation}
      />
    </>
  )
}

CustomerPortalContactsActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  accountId: PropTypes.string,
  selectedContact: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

export default CustomerPortalContactsActionMenu
